import React, { useState, useCallback, useEffect } from "react";
import Swal from "sweetalert2";
import { useNavigate, useParams, Link, Navigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import PageTitle from "../../../layouts/PageTitle";
import { updateWorkProgressApi } from "../../../../services/workprogressService";
import {
  appApi,
  useGetWorkProgressReportQuery,
  useGetWorkProgressDropdownsQuery,
} from "../../../../store/appApi";
import { selectActiveProjectId, selectActiveProjectName } from "../../../../store/projectSlice";
import { selectHasModuleAccess } from "../../../../store/authSlice";

// ── Constants ─────────────────────────────────────────────────────────────────
const LABOUR_TYPES = ["Mistry", "Baildar", "Culi"];

const EMPTY_EQUIPMENT  = { equipmentId: "", eqpQuantity: "" };
const EMPTY_WORK       = { typeOfWork: "", labourEngaged: "", numberOfLabour: "" };
const EMPTY_CONTRACTOR = { contractorId: "", skilledLabour: "", unskilledLabour: "", workAssigned: "" };
const EMPTY_EMPLOYEE   = { employeeId: "", locationName: "" };
const EMPTY_MATERIAL   = { materialId: "", qtyIssued: "", issuedToContractorId: "", _originalQtyIssued: 0 };

// ── toId: safely stringify an ObjectId or plain string ───────────────────────
const toId = (v) => (v && typeof v === "object" ? v._id?.toString() ?? "" : v ?? "");

const EditWorkProgress = () => {
  const { id }    = useParams();
  const navigate  = useNavigate();
  const dispatch  = useDispatch();
  const activeProjectId   = useSelector(selectActiveProjectId);
  const activeProjectName = useSelector(selectActiveProjectName);
  const hasWpWrite = useSelector(selectHasModuleAccess("workProgress", "read-write"));

  const [form, setForm]         = useState(null);   // null = not yet seeded from API
  const [errors, setErrors]     = useState({});
  const [submitting, setSubmitting] = useState(false);

  // ── Remote data ───────────────────────────────────────────────────────────
  const {
    data: reportData,
    isLoading: reportLoading,
    isError: reportError,
  } = useGetWorkProgressReportQuery(id);

  const {
    data: dropdowns,
    isLoading: dropdownsLoading,
  } = useGetWorkProgressDropdownsQuery();

  const equipments  = dropdowns?.equipments  ?? [];
  const contractors = dropdowns?.contractors ?? [];
  const staff       = (dropdowns?.staff ?? []).filter(
    (s) => s.role?.toLowerCase() !== "super-admin",
  );
  const materials   = dropdowns?.materials   ?? [];

  // ── Seed form state from the loaded report (runs once) ───────────────────
  useEffect(() => {
    const r = reportData?.report;
    if (!r || form !== null) return;      // wait for data; don't re-seed

    setForm({
      projectId:      toId(r.projectId),
      reportType:     r.reportType ?? "",
      reportDateTime: r.reportDateTime
        ? new Date(r.reportDateTime).toISOString().slice(0, 16)
        : "",

      equipments: r.equipments?.length
        ? r.equipments.map((e) => ({
            equipmentId: toId(e.equipmentId),
            eqpQuantity: e.eqpQuantity ?? "",
          }))
        : [{ ...EMPTY_EQUIPMENT }],

      workExecution: r.workExecution?.length
        ? r.workExecution.map((w) => ({
            typeOfWork:     w.typeOfWork ?? "",
            labourEngaged:  w.labourEngaged ?? "",
            numberOfLabour: w.numberOfLabour ?? "",
          }))
        : [{ ...EMPTY_WORK }],

      contractorDetails: r.contractorDetails?.length
        ? r.contractorDetails.map((c) => ({
            contractorId:    toId(c.contractorId),
            skilledLabour:   c.skilledLabour ?? "",
            unskilledLabour: c.unskilledLabour ?? "",
            workAssigned:    c.workAssigned ?? "",
          }))
        : [{ ...EMPTY_CONTRACTOR }],

      employeeDetails: r.employeeDetails?.length
        ? r.employeeDetails.map((emp) => ({
            employeeId:   toId(emp.employeeId),
            locationName: emp.locationName ?? "",
          }))
        : [{ ...EMPTY_EMPLOYEE }],

      // Keep _originalQtyIssued so the validation can compute effective available
      materialDetails: r.materialDetails?.length
        ? r.materialDetails.map((m) => ({
            materialId:           toId(m.materialId),
            qtyIssued:            m.qtyIssued ?? "",
            issuedToContractorId: toId(m.issuedToContractorId),
            _originalQtyIssued:   m.qtyIssued ?? 0,
          }))
        : [{ ...EMPTY_MATERIAL }],
    });
  }, [reportData, form]);

  // ── Generic handlers ──────────────────────────────────────────────────────
  const handleField = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleArrayField = useCallback((section, index, field, value) => {
    setForm((prev) => {
      const next = prev[section].map((row, i) =>
        i === index ? { ...row, [field]: value } : row
      );
      return { ...prev, [section]: next };
    });
  }, []);

  const addRow = useCallback((section, template) => {
    setForm((prev) => ({
      ...prev,
      [section]: [...prev[section], { ...template }],
    }));
  }, []);

  const removeRow = useCallback((section, index) => {
    setForm((prev) => ({
      ...prev,
      [section]: prev[section].filter((_, i) => i !== index),
    }));
  }, []);

  // ── Material stock guard ──────────────────────────────────────────────────
  // Effective available = live balance from dropdowns + whatever was originally
  // issued on this line (since that deduction is already in the ledger).
  const effectiveAvailable = (row) => {
    const liveBalance =
      materials.find((m) => m._id === row.materialId)?.currentBalance ?? null;
    if (liveBalance === null) return null;
    return liveBalance + Number(row._originalQtyIssued ?? 0);
  };

  const materialError = (row) => {
    if (!row.materialId || !row.qtyIssued) return null;
    const avail = effectiveAvailable(row);
    if (avail === null) return null;
    if (Number(row.qtyIssued) > avail)
      return `Exceeds available stock (${avail} units including original issue).`;
    return null;
  };

  // ── Validation ────────────────────────────────────────────────────────────
  const validate = () => {
    const e = {};
    if (!activeProjectId)     e.projectId      = "No active project. Select a project from the dashboard.";
    if (!form.reportType)     e.reportType     = "Report type is required.";
    if (!form.reportDateTime) e.reportDateTime = "Date & time is required.";
    if (form.materialDetails.some((row) => materialError(row) !== null))
      e.materialDetails = "Fix quantity errors before submitting.";
    return e;
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: Object.values(validationErrors)[0],
      });
      return;
    }

    const payload = {
      projectId:      activeProjectId,
      reportType:     form.reportType,
      reportDateTime: form.reportDateTime,

      equipments: form.equipments
        .filter((r) => r.equipmentId)
        .map((r) => ({ equipmentId: r.equipmentId, eqpQuantity: Number(r.eqpQuantity) })),

      workExecution: form.workExecution
        .filter((r) => r.typeOfWork || r.labourEngaged)
        .map((r) => ({
          typeOfWork:     r.typeOfWork,
          labourEngaged:  r.labourEngaged,
          numberOfLabour: Number(r.numberOfLabour),
        })),

      contractorDetails: form.contractorDetails
        .filter((r) => r.contractorId)
        .map((r) => ({
          contractorId:    r.contractorId,
          skilledLabour:   Number(r.skilledLabour),
          unskilledLabour: Number(r.unskilledLabour),
          workAssigned:    r.workAssigned,
        })),

      employeeDetails: form.employeeDetails
        .filter((r) => r.employeeId)
        .map((r) => ({ employeeId: r.employeeId, locationName: r.locationName })),

      materialDetails: form.materialDetails
        .filter((r) => r.materialId && r.qtyIssued)
        .map((r) => ({
          materialId:           r.materialId,
          qtyIssued:            Number(r.qtyIssued),
          issuedToContractorId: r.issuedToContractorId || undefined,
        })),
    };

    try {
      setSubmitting(true);
      await updateWorkProgressApi(id, payload);

      // Invalidate both the list and this specific report in RTK cache
      dispatch(appApi.util.invalidateTags([
        "WorkProgressReports",
        "WorkProgressDropdowns", // balances changed
      ]));

      Swal.fire({
        icon: "success",
        title: "Report Updated!",
        text: "The daily progress report has been saved.",
        confirmButtonText: "View Report",
      }).then(() => navigate(`/progress/workprogress/detail/${id}`));
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Update Failed",
        text: err.response?.data?.message ?? "Something went wrong. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (!hasWpWrite) return <Navigate to="../list" replace />;

  // ── Loading & error states ────────────────────────────────────────────────
  const pageLoading = reportLoading || dropdownsLoading || form === null;

  if (pageLoading) {
    return (
      <>
        <PageTitle activeMenu="Edit Report" motherMenu="Daily Progress" />
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status" />
          <p className="mt-2 text-muted">Loading report…</p>
        </div>
      </>
    );
  }

  if (reportError) {
    return (
      <>
        <PageTitle activeMenu="Edit Report" motherMenu="Daily Progress" />
        <div className="alert alert-danger">
          Failed to load report.{" "}
          <Link to="/progress/workprogress/list">Back to list</Link>
        </div>
      </>
    );
  }

  // ── Section header (same style as Add.jsx) ────────────────────────────────
  const SectionHeader = ({ number, title }) => (
    <div className="card-header bg-primary bg-opacity-10 d-flex align-items-center gap-2 py-2">
      <span
        className="badge bg-primary rounded-circle d-flex align-items-center justify-content-center"
        style={{ width: 28, height: 28, fontSize: 13 }}
      >
        {number}
      </span>
      <h5 className="mb-0 text-primary fw-semibold">{title}</h5>
    </div>
  );

  return (
    <>
      <PageTitle activeMenu="Edit Report" motherMenu="Daily Progress" />

      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">Editing Daily Progress Report</h5>
        <Link
          to={`/progress/workprogress/detail/${id}`}
          className="btn btn-light btn-sm"
        >
          ← Back to Detail
        </Link>
      </div>

      <form onSubmit={handleSubmit} noValidate>

        {/* ── Section 1: Core Meta ──────────────────────────────────────── */}
        <div className="card mb-4">
          <SectionHeader number={1} title="Core Report Details" />
          <div className="card-body">
            <div className="row g-3">

              <div className="col-md-4">
                <label className="form-label">Project</label>
                {activeProjectName ? (
                  <div
                    className="form-control d-flex align-items-center gap-2"
                    style={{ background: "#3243360d", border: "1px solid #32433640", color: "#324336", fontWeight: 600 }}
                  >
                    <i className="fas fa-building" style={{ fontSize: 14 }} />
                    {activeProjectName}
                  </div>
                ) : (
                  <div className="alert alert-warning py-2 mb-0" style={{ fontSize: 13 }}>
                    No project selected. <a href="/dashboard">Select a project</a> first.
                  </div>
                )}
                {errors.projectId && (
                  <small className="text-danger d-block mt-1">{errors.projectId}</small>
                )}
              </div>

              <div className="col-md-4">
                <label className="form-label">
                  Shift <span className="text-danger">*</span>
                </label>
                <select
                  name="reportType"
                  className={`form-control${errors.reportType ? " is-invalid" : ""}`}
                  value={form.reportType}
                  onChange={handleField}
                >
                  <option value="">Select Shift</option>
                  <option value="morning">Morning Shift</option>
                  <option value="evening">Evening Shift</option>
                </select>
                {errors.reportType && (
                  <div className="invalid-feedback">{errors.reportType}</div>
                )}
              </div>

              <div className="col-md-4">
                <label className="form-label">
                  Report Date &amp; Time <span className="text-danger">*</span>
                </label>
                <input
                  type="datetime-local"
                  name="reportDateTime"
                  className={`form-control${errors.reportDateTime ? " is-invalid" : ""}`}
                  value={form.reportDateTime}
                  onChange={handleField}
                />
                {errors.reportDateTime && (
                  <div className="invalid-feedback">{errors.reportDateTime}</div>
                )}
              </div>

            </div>
          </div>
        </div>

        {/* ── Section 2A: Equipment ─────────────────────────────────────── */}
        <div className="card mb-4">
          <SectionHeader number="2A" title="Equipment Deployment" />
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-bordered align-middle mb-2">
                <thead className="table-light">
                  <tr>
                    <th>Equipment</th>
                    <th style={{ width: 160 }}>Qty Deployed</th>
                    <th style={{ width: 50 }} />
                  </tr>
                </thead>
                <tbody>
                  {form.equipments.map((row, i) => (
                    <tr key={i}>
                      <td>
                        <select
                          className="form-control form-control-sm"
                          value={row.equipmentId}
                          onChange={(e) =>
                            handleArrayField("equipments", i, "equipmentId", e.target.value)
                          }
                        >
                          <option value="">Select Equipment</option>
                          {equipments.map((eq) => (
                            <option key={eq._id} value={eq._id}>
                              {eq.eqpName}
                              {eq.eqpType ? ` — ${eq.eqpType}` : ""}
                              {eq.eqpNumber ? ` (${eq.eqpNumber})` : ""}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <input
                          type="number"
                          min="0"
                          className="form-control form-control-sm"
                          value={row.eqpQuantity}
                          onChange={(e) =>
                            handleArrayField("equipments", i, "eqpQuantity", e.target.value)
                          }
                        />
                      </td>
                      <td className="text-center">
                        {form.equipments.length > 1 && (
                          <button
                            type="button"
                            className="btn btn-danger btn-xs py-0 px-1"
                            onClick={() => removeRow("equipments", i)}
                          >
                            &times;
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button
              type="button"
              className="btn btn-outline-primary btn-sm"
              onClick={() => addRow("equipments", EMPTY_EQUIPMENT)}
            >
              + Add Equipment Row
            </button>
          </div>
        </div>

        {/* ── Section 2B: Work Execution ────────────────────────────────── */}
        <div className="card mb-4">
          <SectionHeader number="2B" title="Work Execution &amp; Manpower" />
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-bordered align-middle mb-2">
                <thead className="table-light">
                  <tr>
                    <th>Type of Work</th>
                    <th>Labour Engaged</th>
                    <th style={{ width: 140 }}>No. of Labour</th>
                    <th style={{ width: 50 }} />
                  </tr>
                </thead>
                <tbody>
                  {form.workExecution.map((row, i) => (
                    <tr key={i}>
                      <td>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={row.typeOfWork}
                          onChange={(e) =>
                            handleArrayField("workExecution", i, "typeOfWork", e.target.value)
                          }
                          placeholder="e.g. Brick laying"
                        />
                      </td>
                      <td>
                        <select
                          className="form-control form-control-sm"
                          value={row.labourEngaged}
                          onChange={(e) =>
                            handleArrayField("workExecution", i, "labourEngaged", e.target.value)
                          }
                        >
                          <option value="">Select Labour</option>
                          {LABOUR_TYPES.map((lt) => (
                            <option key={lt} value={lt}>{lt}</option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <input
                          type="number"
                          min="0"
                          className="form-control form-control-sm"
                          value={row.numberOfLabour}
                          onChange={(e) =>
                            handleArrayField("workExecution", i, "numberOfLabour", e.target.value)
                          }
                        />
                      </td>
                      <td className="text-center">
                        {form.workExecution.length > 1 && (
                          <button
                            type="button"
                            className="btn btn-danger btn-xs py-0 px-1"
                            onClick={() => removeRow("workExecution", i)}
                          >
                            &times;
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button
              type="button"
              className="btn btn-outline-primary btn-sm"
              onClick={() => addRow("workExecution", EMPTY_WORK)}
            >
              + Add Work Row
            </button>
          </div>
        </div>

        {/* ── Section 3: Contractor Engagement ─────────────────────────── */}
        <div className="card mb-4">
          <SectionHeader number={3} title="Contractor Engagement" />
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-bordered align-middle mb-2">
                <thead className="table-light">
                  <tr>
                    <th>Contractor</th>
                    <th style={{ width: 130 }}>Skilled</th>
                    <th style={{ width: 130 }}>Unskilled</th>
                    <th>Work Assigned</th>
                    <th style={{ width: 50 }} />
                  </tr>
                </thead>
                <tbody>
                  {form.contractorDetails.map((row, i) => (
                    <tr key={i}>
                      <td>
                        <select
                          className="form-control form-control-sm"
                          value={row.contractorId}
                          onChange={(e) =>
                            handleArrayField("contractorDetails", i, "contractorId", e.target.value)
                          }
                        >
                          <option value="">Select Contractor</option>
                          {contractors.map((c) => (
                            <option key={c._id} value={c._id}>
                              {c.name}{c.phone ? ` — ${c.phone}` : ""}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <input type="number" min="0" className="form-control form-control-sm"
                          value={row.skilledLabour}
                          onChange={(e) => handleArrayField("contractorDetails", i, "skilledLabour", e.target.value)}
                        />
                      </td>
                      <td>
                        <input type="number" min="0" className="form-control form-control-sm"
                          value={row.unskilledLabour}
                          onChange={(e) => handleArrayField("contractorDetails", i, "unskilledLabour", e.target.value)}
                        />
                      </td>
                      <td>
                        <input type="text" className="form-control form-control-sm"
                          value={row.workAssigned}
                          onChange={(e) => handleArrayField("contractorDetails", i, "workAssigned", e.target.value)}
                          placeholder="Describe work"
                        />
                      </td>
                      <td className="text-center">
                        {form.contractorDetails.length > 1 && (
                          <button type="button" className="btn btn-danger btn-xs py-0 px-1"
                            onClick={() => removeRow("contractorDetails", i)}>
                            &times;
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button type="button" className="btn btn-outline-primary btn-sm"
              onClick={() => addRow("contractorDetails", EMPTY_CONTRACTOR)}>
              + Add Contractor Row
            </button>
          </div>
        </div>

        {/* ── Section 4: Internal Staff ─────────────────────────────────── */}
        <div className="card mb-4">
          <SectionHeader number={4} title="Internal Staff Deployment" />
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-bordered align-middle mb-2">
                <thead className="table-light">
                  <tr>
                    <th>Employee (Name — Role)</th>
                    <th>Location / Zone</th>
                    <th style={{ width: 50 }} />
                  </tr>
                </thead>
                <tbody>
                  {form.employeeDetails.map((row, i) => (
                    <tr key={i}>
                      <td>
                        <select
                          className="form-control form-control-sm"
                          value={row.employeeId}
                          onChange={(e) =>
                            handleArrayField("employeeDetails", i, "employeeId", e.target.value)
                          }
                        >
                          <option value="">Select Employee</option>
                          {staff.map((s) => (
                            <option key={s._id} value={s._id}>
                              {s.name}{s.role ? ` — ${s.role}` : ""}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <input type="text" className="form-control form-control-sm"
                          value={row.locationName}
                          onChange={(e) => handleArrayField("employeeDetails", i, "locationName", e.target.value)}
                          placeholder="e.g. Block A – 3rd Floor"
                        />
                      </td>
                      <td className="text-center">
                        {form.employeeDetails.length > 1 && (
                          <button type="button" className="btn btn-danger btn-xs py-0 px-1"
                            onClick={() => removeRow("employeeDetails", i)}>
                            &times;
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button type="button" className="btn btn-outline-primary btn-sm"
              onClick={() => addRow("employeeDetails", EMPTY_EMPLOYEE)}>
              + Add Staff Row
            </button>
          </div>
        </div>

        {/* ── Section 5: Material Issuance ──────────────────────────────── */}
        <div className="card mb-4">
          <SectionHeader number={5} title="Material Issuance &amp; Stock Allocation" />
          <div className="card-body">
            {errors.materialDetails && (
              <div className="alert alert-danger py-2 mb-3">{errors.materialDetails}</div>
            )}
            <div className="table-responsive">
              <table className="table table-bordered align-middle mb-2">
                <thead className="table-light">
                  <tr>
                    <th>Material (Available Stock)</th>
                    <th style={{ width: 160 }}>Qty Issued</th>
                    <th>Issued To (Contractor)</th>
                    <th style={{ width: 50 }} />
                  </tr>
                </thead>
                <tbody>
                  {form.materialDetails.map((row, i) => {
                    const stockErr  = materialError(row);
                    const avail     = effectiveAvailable(row);
                    const selectedMat = materials.find((m) => m._id === row.materialId);
                    return (
                      <tr key={i}>
                        <td>
                          <select
                            className="form-control form-control-sm"
                            value={row.materialId}
                            onChange={(e) =>
                              handleArrayField("materialDetails", i, "materialId", e.target.value)
                            }
                          >
                            <option value="">Select Material</option>
                            {materials.map((m) => (
                              <option key={m._id} value={m._id}>
                                {m.skuName}
                                {m.measurement ? ` (${m.measurement})` : ""}
                                {" — "}{m.currentBalance} in stock
                              </option>
                            ))}
                          </select>
                          {selectedMat && avail !== null && (
                            <small className="text-muted d-block mt-1">
                              Max you can set:{" "}
                              <strong>
                                {avail} {selectedMat.measurement}
                              </strong>
                              {row._originalQtyIssued > 0 && (
                                <span className="text-info ms-1">
                                  (includes {row._originalQtyIssued} originally issued)
                                </span>
                              )}
                            </small>
                          )}
                        </td>
                        <td>
                          <input
                            type="number"
                            min="0"
                            className={`form-control form-control-sm${stockErr ? " is-invalid" : ""}`}
                            value={row.qtyIssued}
                            onChange={(e) =>
                              handleArrayField("materialDetails", i, "qtyIssued", e.target.value)
                            }
                          />
                          {stockErr && (
                            <div className="text-danger" style={{ fontSize: "0.75rem", marginTop: 3 }}>
                              ⚠ {stockErr}
                            </div>
                          )}
                        </td>
                        <td>
                          <select
                            className="form-control form-control-sm"
                            value={row.issuedToContractorId}
                            onChange={(e) =>
                              handleArrayField("materialDetails", i, "issuedToContractorId", e.target.value)
                            }
                          >
                            <option value="">Select Contractor (optional)</option>
                            {contractors.map((c) => (
                              <option key={c._id} value={c._id}>{c.name}</option>
                            ))}
                          </select>
                        </td>
                        <td className="text-center">
                          {form.materialDetails.length > 1 && (
                            <button
                              type="button"
                              className="btn btn-danger btn-xs py-0 px-1"
                              onClick={() => removeRow("materialDetails", i)}
                            >
                              &times;
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <button type="button" className="btn btn-outline-primary btn-sm"
              onClick={() => addRow("materialDetails", EMPTY_MATERIAL)}>
              + Add Material Row
            </button>
          </div>
        </div>

        {/* ── Submit ─────────────────────────────────────────────────────── */}
        <div className="d-flex gap-2 mb-5">
          <button type="submit" className="btn btn-primary px-4" disabled={submitting}>
            {submitting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Saving…
              </>
            ) : (
              "Save Changes"
            )}
          </button>
          <Link
            to={`/progress/workprogress/detail/${id}`}
            className="btn btn-light px-4"
          >
            Cancel
          </Link>
        </div>

      </form>
    </>
  );
};

export default EditWorkProgress;
